import React, { useMemo, lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import menu from '../../../redux/json/menu.json';
import company from '../../../redux/json/company.json';

// Lazy load icons
const FaEnvelope = lazy(() => import("react-icons/fa").then(module => ({ default: module.FaEnvelope })));
const FaMobileAlt = lazy(() => import("react-icons/fa").then(module => ({ default: module.FaMobileAlt })));
const FaLocationArrow = lazy(() => import("react-icons/fa6").then(module => ({ default: module.FaLocationArrow })));

const FooterLayout = () => {
    const menuListData = useSelector(state => state.webSetting.menuListData || menu);
    const companyData = useSelector(state => state.webSetting.companyData || company);

    const description = useMemo(() => companyData?.description || "STEMCITY® empowers young minds with DIY STEM, Robotics & AI kits—making learning fun, hands-on, and innovative for schools, homes, and future creators. Learn by doing, build your future!", [companyData]);
    const logoSrc = useMemo(() => companyData?.logo, [companyData]);

    const { quickLinks, importantLinks } = useMemo(() => {
        const quickLinks = menuListData?.filter(item => item.footer_menu === "1");
        const importantLinks = menuListData?.filter(item => item.second_footer_menu === "1");
        return { quickLinks, importantLinks };
    }, [menuListData]);

    const IconWrapper = ({ Icon, className = "", size = 20, ...props }) => (
        <Suspense
            fallback={
                <div
                    className={`inline-block min-w-[${size}px] min-h-[${size}px] w-[${size}px] h-[${size}px]`}
                />
            }
        >
            <Icon
                {...props}
                className={`${className} inline-block min-w-[${size}px] min-h-[${size}px] w-[${size}px] h-[${size}px]`}
                size={size}
            />
        </Suspense>
    );


    const RenderLinks = ({ title, links }) => {
        if (!links?.length) return null;
        return (
            <div className="py-8 px-4">
                <h2 className="text-xl font-bold mb-3">{title}</h2>
                <ul className="space-y-3">
                    {links.map((link, idx) => (
                        <li key={idx}>
                            <Link
                                to={link.url || "#"}
                                className="text-gray dark:text-gray font-semibold hover:text-primary duration-300"
                            >
                                {link.name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const RenderContactItem = ({ href, Icon, text }) => (
        <div className="min-h-[24px] flex items-center gap-3">
            {text ? (
                <a href={href} target="_blank" rel="noopener noreferrer">
                    <div className="flex items-center gap-3">
                        <IconWrapper Icon={Icon} className="text-gray hover:text-primary" size={20} />
                        <p className="text-gray font-semibold hover:text-primary">{text}</p>
                    </div>
                </a>
            ) : (
                <div className="w-24 h-3 bg-gray-200 rounded animate-pulse"></div>
            )}
        </div>
    );

    return (
            <div className="grid md:grid-cols-3">
                {/* Logo and Description */}
                <div className="py-8 px-4 flex flex-col items-start gap-3">
                    {logoSrc && (
                        <div className="w-24 h-[72px] overflow-hidden shrink-0 relative">
                            <img
                                src={logoSrc}
                                width="96"
                                height="72"
                                alt={`${companyData?.name || 'Company'} footer logo - STEM Learning`}
                                title={`${companyData?.name || 'Company'} footer logo - STEM Learning`}
                                className="w-full h-full object-contain"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    )}
                    <p className="text-black dark:text-gray min-h-[60px] text-base leading-relaxed">
                        {description}
                    </p>
                </div>

                {/* Links and Contact */}
                <div className="col-span-2 grid grid-cols-2 sm:grid-cols-3 md:pl-10">
                    <RenderLinks title="Quick Links" links={quickLinks} />
                    <RenderLinks title="Important Links" links={importantLinks} />

                    {/* Contact Info */}
                    <div className="py-8 px-4 col-span-2 sm:col-auto">
                        <h2 className="text-xl font-bold mb-3">Contact</h2>
                        <div className="space-y-6">
                            <RenderContactItem
                                href={companyData?.map_link || "#"}
                                Icon={FaLocationArrow}
                                text={companyData?.address}
                            />
                            <RenderContactItem
                                href={`tel:${companyData?.phone}`}
                                Icon={FaMobileAlt}
                                text={companyData?.phone}
                            />
                            <RenderContactItem
                                href={`mailto:${companyData?.email}`}
                                Icon={FaEnvelope}
                                text={companyData?.email}
                            />
                            <RenderContactItem
                                href={`tel:${companyData?.landline}`}
                                Icon={FaMobileAlt}
                                text={companyData?.landline}
                            />
                        </div>
                    </div>
                </div>
            </div>
    );
};

export default React.memo(FooterLayout);
